import { useState } from "react";
import PageHeader from "@/components/base/PageHeader";
import Card, { SectionTitle } from "@/components/base/Card";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import Modal from "@/components/base/Modal";
import StatCard from "@/components/base/StatCard";
import { Input } from "@/components/base/Field";
import { ErrorState, LoadingState } from "@/components/base/StateViews";
import { useToast } from "@/components/base/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { useAsync } from "@/hooks/useAsync";
import { getForecast, setOpening, type FlowKind } from "@/services/treasuryService";
import { currencySymbol } from "@/utils/currency";
import { formatDate, formatMoney } from "@/utils/format";
import BalanceChart from "./components/BalanceChart";

const HORIZONS = [30, 60, 90] as const;
const KIND: Record<FlowKind, { label: string; tone: "success" | "secondary" | "danger" | "neutral"; sign: 1 | -1 }> = {
  ECHEANCE: { label: "Frais attendus", tone: "success", sign: 1 },
  RETARD: { label: "Retards rattrapés", tone: "secondary", sign: 1 },
  PAIE: { label: "Paie", tone: "danger", sign: -1 },
  CHARGE: { label: "Charge", tone: "neutral", sign: -1 },
};
const pct = (n: number) => `${Math.round(n * 100)} %`;
const todayISO = () => new Date().toISOString().slice(0, 10);

/** Prévision de trésorerie : solde prévu jour par jour, entrées et sorties attendues, hypothèses utilisées. */
export default function TreasuryPage() {
  const { notify } = useToast();
  const { can } = useAuth();
  const [days, setDays] = useState<(typeof HORIZONS)[number]>(60);
  const [editing, setEditing] = useState(false);
  const [opening, setOpeningForm] = useState({ amount: "", date: todayISO() });
  const [saving, setSaving] = useState(false);
  const { data, loading, error, reload } = useAsync(() => getForecast(days), [days]);

  if (loading && !data) return <LoadingState label="Calcul de la prévision..." />;
  if (error || !data) return <ErrorState message={error ?? "Chargement impossible."} onRetry={reload} />;

  const openEditor = () => {
    setOpeningForm({ amount: data.opening ? String(data.opening.amount) : "", date: data.opening?.date ?? todayISO() });
    setEditing(true);
  };

  const saveOpening = async () => {
    if (opening.amount === "" || Number.isNaN(Number(opening.amount))) {
      notify("Indiquez le montant disponible.", "error");
      return;
    }
    setSaving(true);
    try {
      await setOpening(Number(opening.amount), opening.date);
      notify("Solde de départ enregistré.");
      setEditing(false);
      reload();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Enregistrement impossible.", "error");
    } finally {
      setSaving(false);
    }
  };

  const editor = (
    <Modal
      open={editing}
      onClose={() => setEditing(false)}
      title="Solde de départ"
      icon="ri-safe-2-line"
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={() => setEditing(false)}>
            Annuler
          </Button>
          <Button icon="ri-save-3-line" onClick={saveOpening} disabled={saving}>
            {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-foreground-600">
          L'argent réellement disponible (caisse + banque) au matin de la date choisie. Ensuite, le solde suit tout seul : on
          ajoute les recettes et on retire les dépenses enregistrées depuis cette date.
        </p>
        <Input
          label={`Montant disponible (${currencySymbol()})`}
          type="number"
          min={-9_999_999_999}
          value={opening.amount}
          onChange={(e) => setOpeningForm({ ...opening, amount: e.target.value })}
          hint="Négatif en cas de découvert"
        />
        <Input
          label="Constaté le"
          type="date"
          max={todayISO()}
          value={opening.date}
          onChange={(e) => setOpeningForm({ ...opening, date: e.target.value })}
        />
      </div>
    </Modal>
  );

  if (!data.configured) {
    return (
      <div className="animate-fade-in">
        <PageHeader title="Prévision de trésorerie" subtitle="Solde prévu jour par jour" icon="ri-funds-line" />
        <Card className="max-w-2xl">
          <SectionTitle icon="ri-safe-2-line" title="Commencez par le solde de départ" subtitle="Une seule fois" />
          <p className="text-sm text-foreground-600">
            Pour prévoir la trésorerie, l'application doit connaître l'argent dont l'école dispose aujourd'hui (caisse +
            banque). Saisissez-le une fois : ensuite, les recettes et dépenses enregistrées le mettent à jour tout seules.
          </p>
          {can("treasury:update") ? (
            <Button className="mt-4" icon="ri-edit-line" onClick={openEditor}>
              Saisir le solde de départ
            </Button>
          ) : (
            <p className="mt-4 text-sm text-foreground-500">Demandez à un administrateur de saisir le solde de départ.</p>
          )}
        </Card>
        {editor}
      </div>
    );
  }

  const b = data.behaviour;
  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Prévision de trésorerie"
        subtitle={`Solde prévu jour par jour sur ${data.horizon} jours`}
        icon="ri-funds-line"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-md border border-background-300 p-0.5" role="group" aria-label="Période">
              {HORIZONS.map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => setDays(h)}
                  className={`rounded px-3 py-1.5 text-xs font-medium cursor-pointer ${
                    days === h ? "bg-primary-600 text-white" : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  {h} jours
                </button>
              ))}
            </div>
            {can("treasury:update") && (
              <Button variant="outline" icon="ri-edit-line" onClick={openEditor}>
                Solde de départ
              </Button>
            )}
          </div>
        }
      />

      {data.firstNegativeDate ? (
        <p className="mb-4 flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-accent-800">
          <i className="ri-error-warning-line mt-0.5"></i>
          <span>
            Le solde prévu passe sous zéro le <strong>{formatDate(data.firstNegativeDate)}</strong>. Point le plus bas :{" "}
            <strong>{formatMoney(data.minBalance)}</strong> le {formatDate(data.minDate)}. Pistes : relancer les retards, avancer
            une échéance, ou décaler une dépense non urgente.
          </span>
        </p>
      ) : (
        <p className="mb-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
          <i className="ri-checkbox-circle-line mt-0.5"></i>
          <span>
            Le solde reste positif sur les {data.horizon} prochains jours. Point le plus bas : {formatMoney(data.minBalance)} le{" "}
            {formatDate(data.minDate)}.
          </span>
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Solde aujourd'hui"
          value={formatMoney(data.currentBalance)}
          icon="ri-safe-2-line"
          tone="primary"
          hint={data.opening ? `Départ ${formatMoney(data.opening.amount)} le ${formatDate(data.opening.date)}` : undefined}
        />
        <StatCard label="Entrées prévues" value={formatMoney(data.inflow)} icon="ri-arrow-down-circle-line" tone="secondary" />
        <StatCard label="Sorties prévues" value={formatMoney(data.outflow)} icon="ri-arrow-up-circle-line" tone="accent" />
        <StatCard
          label={`Solde dans ${data.horizon} jours`}
          value={formatMoney(data.endBalance)}
          icon="ri-funds-line"
          tone={data.endBalance < 0 ? "accent" : "primary"}
        />
      </div>

      <Card className="mt-5">
        <SectionTitle icon="ri-line-chart-line" title="Solde prévu" subtitle="Ligne pointillée : zéro ; point rouge : le plus bas" />
        <BalanceChart forecast={data} />
      </Card>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-2" padded={false}>
          <div className="border-b border-background-200 p-4">
            <p className="text-sm font-semibold text-foreground-900">Entrées et sorties attendues</p>
          </div>
          {data.flows.length === 0 ? (
            <p className="p-6 text-sm text-foreground-500">Aucune entrée ni sortie prévue sur la période.</p>
          ) : (
            <div className="max-h-[480px] overflow-auto">
              <table className="w-full min-w-[560px] text-sm">
                <thead className="sticky top-0 bg-background-100 text-left text-xs uppercase tracking-wide text-foreground-500">
                  <tr>
                    <th className="px-4 py-2.5 font-medium">Date</th>
                    <th className="px-3 py-2.5 font-medium">Type</th>
                    <th className="px-3 py-2.5 font-medium">Détail</th>
                    <th className="px-4 py-2.5 font-medium text-right">Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {data.flows.map((f, i) => (
                    <tr key={`${f.date}-${f.kind}-${i}`} className="border-t border-background-200">
                      <td className="px-4 py-2.5 whitespace-nowrap">{formatDate(f.date)}</td>
                      <td className="px-3 py-2.5">
                        <Badge tone={KIND[f.kind].tone}>{KIND[f.kind].label}</Badge>
                      </td>
                      <td className="px-3 py-2.5 text-foreground-700">{f.label}</td>
                      <td className={`px-4 py-2.5 text-right font-medium tabular-nums ${KIND[f.kind].sign > 0 ? "text-primary-700" : "text-accent-700"}`}>
                        {KIND[f.kind].sign > 0 ? "+" : "−"} {formatMoney(f.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card>
          <SectionTitle icon="ri-questionnaire-line" title="Comment c'est calculé" />
          <ul className="space-y-2.5 text-sm text-foreground-600">
            <li>
              <strong className="text-foreground-800">Frais attendus</strong> : chaque échéance datée de la fiche, répartie selon
              la ponctualité des familles :
              <span className="mt-1 block text-xs">
                {pct(b.onTime)} à l'échéance · {pct(b.within30)} sous 30 jours · {pct(b.within60)} sous 60 jours
              </span>
              <span className="mt-1 block text-xs text-foreground-500">
                {b.source === "history"
                  ? `Mesuré sur ${b.sample} frais déjà échus de l'école.`
                  : "Hypothèse prudente : pas encore assez d'échéances passées pour mesurer vos familles."}
              </span>
            </li>
            <li>
              <strong className="text-foreground-800">Retards</strong> : seule la part que les familles finissent habituellement
              par payer est comptée. Au-delà de 60 jours de retard, rien n'est compté
              {data.doubtful > 0 ? ` (${formatMoney(data.doubtful)} de retards anciens laissés de côté)` : ""}.
            </li>
            <li>
              <strong className="text-foreground-800">Paie</strong> : le salaire attendu de chaque mois, moins ce qui est déjà
              payé, au jour habituel de paie.
            </li>
            <li>
              <strong className="text-foreground-800">Charges</strong> : le budget mensuel de chaque charge, moins ce qui est déjà
              dépensé dans le mois, au jour habituel de paiement.
            </li>
            <li className="text-xs text-foreground-500">
              Les frais sans date (« À l'inscription ») ne sont pas comptés : datez les échéances dans la fiche de renseignements
              pour une prévision plus juste.
            </li>
          </ul>
        </Card>
      </div>
      {editor}
    </div>
  );
}
